import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppointmentsListQuery, useAuthProfileRetrieveQuery, useTeamsTeamMembersMemberAppointmentsRetrieveQuery, useTeamsTeamMembersByServiceSalonRetrieveQuery, useAppointmentsAvailableSlotsRetrieveQuery, useServicesSalonListQuery } from '../../../services/GLOWClient';
import { Card, CardContent, CardHeader, Alert, Box, Typography, CircularProgress, Button, ToggleButtonGroup, ToggleButton, IconButton } from '@mui/material';
import { Add, ChevronLeft, ChevronRight, CalendarToday, ViewWeek, ViewDay } from '@mui/icons-material';
import { theme } from '../../../theme';
import { TitleScreen } from '../../../SystemDesign/styles';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/ar';
import 'moment/locale/en-gb';
import DeleteAppointment from './deleteAppointment';
import UpdateAppointment from './updateAppointment';
import AddAppointment from './addAppointment';
import DayView from './DayView';
import WeekView from './WeekView';
import MonthView from './MonthView';
import { useAuth } from '../../../context/AuthContext';
import Can from '../../../permissions/Can';
import { useSearchParams } from 'react-router-dom';
import AppointmentProposalDialog from './AppointmentProposalDialog';
import AppointmentDetailsDialog from './AppointmentDetailsDialog';
import {
  useConfirmAppointmentMutation,
  useProposeAppointmentMutation,
} from '../../../services/appointmentWorkflowApi';
import { resolveAppointmentDeepLink } from '../../../utils/appointmentNotificationNavigation';
import { appointmentStatusLabelKey } from '../../../utils/appointmentWorkflow';
import {
  buildProposalAvailabilityArgs,
  createInitialProposalForm,
  getEntityId,
} from '../../../utils/appointmentProposalForm';

const Appointment = () => {
  const { t, i18n } = useTranslation();

  // Configure moment locale immediately and on language change
  const setMomentLocale = () => {
    const momentLocale = i18n.language === 'ar' ? 'ar' : i18n.language === 'en' ? 'en-gb' : 'fr';
    moment.locale(momentLocale);
    
    // For Arabic, use Latin numerals instead of Arabic numerals
    if (i18n.language === 'ar') {
      moment.updateLocale(momentLocale, {
        preparse: function (string) {
          return string.replace(/[٠-٩]/g, function (match) {
            return match.charCodeAt(0) - 0x0660;
          });
        },
        postformat: function (string) {
          return string.replace(/\d/g, function (match) {
            return String.fromCharCode(match.charCodeAt(0) + 0x0660);
          });
        }
      });
      // Override postformat to keep Latin numerals
      moment.updateLocale(momentLocale, {
        postformat: function (string) {
          return string; // Keep original string with Latin numerals
        }
      });
    }
  };
  
  // Set locale immediately on mount
  setMomentLocale();
  
  // Also update locale when language changes
  useEffect(() => {
    setMomentLocale();
  }, [i18n.language]);

  const { user } = useAuth();
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [appointmentToUpdate, setAppointmentToUpdate] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [workflowFilter, setWorkflowFilter] = useState('all');
  const [workflowError, setWorkflowError] = useState('');
  const [deepLinkError, setDeepLinkError] = useState('');
  const [proposalAppointment, setProposalAppointment] = useState(null);
  const [proposalServiceId, setProposalServiceId] = useState('');
  const [proposalEmployeeId, setProposalEmployeeId] = useState('');
  const [proposalDate, setProposalDate] = useState('');
  const [confirmAppointment, { isLoading: isConfirming }] = useConfirmAppointmentMutation();
  const [proposeAppointment, { isLoading: isProposing }] = useProposeAppointmentMutation();

  const [searchParams, setSearchParams] = useSearchParams();

  // View mode state (day, week, month)
  const [viewMode, setViewMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(moment());

  // Get user profile data from API
  const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery();

  // Determine if we need to filter by salon based on user role or team member ID
  const queryParams = useMemo(() => {
    if (!userProfile) return {};

    // For salon owners (professional_responsable), filter by their salons
    if (userProfile.role === 'professional_responsable') {
      // Do not send a single-salon filter; backend will return all owned salons' appointments
      return {};
    }

    // For team members (professional_employe), we'll use a different endpoint
    if (userProfile.role === 'professional_employe') {
      // Check if team_member_id exists directly on the user profile
      if (userProfile.team_member_id) {
        return { teamMemberId: userProfile.team_member_id };
      }
      // If not found directly, try to find it in other properties
      if (userProfile.id) {
        return { teamMemberId: userProfile.id };
      }
    }

    // For other users, don't apply salon filter
    // The backend will handle filtering based on user role
    return {};
  }, [userProfile]);

  // Determine which endpoint to use based on user role
  const isTeamMember = userProfile?.role === 'professional_employe';

  // For team members, use the specific team member appointments endpoint
  // Only pass the teamMemberId parameter
  const teamMemberQueryParams =
    isTeamMember && userProfile?.team_member_id &&
      userProfile.team_member_id !== undefined &&
      userProfile.team_member_id !== ''
      ? {
        teamMemberId: userProfile.team_member_id
      }
      : isTeamMember && userProfile?.id &&
        userProfile.id !== undefined &&
        userProfile.id !== ''
        ? {
          teamMemberId: userProfile.id
        }
        : undefined;

  const {
    data: teamMemberAppointments,
    error: teamMemberError,
    isLoading: teamMemberLoading,
    refetch: refetchTeamMember
  } = useTeamsTeamMembersMemberAppointmentsRetrieveQuery(
    teamMemberQueryParams,
    {
      skip: !isTeamMember || !teamMemberQueryParams || profileLoading,
      // Force the response to be treated as an array
      transformResponse: (response) => {
        // If response is already an array, return it
        if (Array.isArray(response)) {
          return response;
        }
        // If response contains a results property that's an array, return that
        if (response && Array.isArray(response.results)) {
          return response.results;
        }
        // Otherwise return an empty array
        return [];
      }
    }
  );

  // For other users, use the general appointments list endpoint
  const {
    data: generalAppointments,
    error: generalError,
    isLoading: generalLoading,
    refetch: refetchGeneral
  } = useAppointmentsListQuery(
    queryParams,
    { skip: isTeamMember || profileLoading }
  );

  // Combine the results based on which endpoint was used
  const appointments = isTeamMember ? teamMemberAppointments : generalAppointments;
  const error = isTeamMember ? teamMemberError : generalError;
  const appointmentsLoading = isTeamMember ? teamMemberLoading : generalLoading;
  const refetch = isTeamMember ? refetchTeamMember : refetchGeneral;

  // Combined loading state
  const isLoading = profileLoading || appointmentsLoading;

  useEffect(() => {
    const rawAppointmentId = searchParams.get('appointmentId');
    if (!rawAppointmentId || isLoading || !Array.isArray(appointments)) return;

    const { appointment: match, errorKey } = resolveAppointmentDeepLink(
      appointments,
      rawAppointmentId,
    );

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('appointmentId');
    setSearchParams(nextParams, { replace: true });

    if (!match) {
      setDeepLinkError(errorKey ? t(errorKey) : '');
      return;
    }

    setDeepLinkError('');

    if (match.start_time) {
      const nextDate = moment(match.start_time);
      if (nextDate.isValid()) {
        setCurrentDate(nextDate);
      }
    }

    setSelectedAppointment(match);
  }, [appointments, isLoading, searchParams, setSearchParams, t]);

  // Filter appointments based on view mode and current date
  const filteredAppointments = useMemo(() => {
    if (!appointments || !Array.isArray(appointments)) return [];

    // For Arabic: week starts on Sunday, for others: week starts on Monday (ISO)
    const weekStart = i18n.language === 'ar' ? 'week' : 'isoWeek';
    
    const startOfPeriod = currentDate.clone().startOf(viewMode === 'day' ? 'day' : viewMode === 'week' ? weekStart : 'month');
    const endOfPeriod = currentDate.clone().endOf(viewMode === 'day' ? 'day' : viewMode === 'week' ? weekStart : 'month');

    return appointments.filter(appointment => {
      // Parse the appointment date and convert to local time for consistent comparison
      const appointmentDate = moment(appointment.start_time);
      return appointmentDate.isBetween(startOfPeriod, endOfPeriod, null, '[]');
    });
  }, [appointments, currentDate, viewMode, i18n.language]);

  const actionRequiredCount = useMemo(
    () => (appointments || []).filter((item) => ['pending', 'proposal_expired'].includes(item.status)).length,
    [appointments],
  );
  const visibleAppointments = useMemo(() => {
    if (workflowFilter !== 'action_required') return filteredAppointments;
    return filteredAppointments.filter((item) => ['pending', 'proposal_expired'].includes(item.status));
  }, [filteredAppointments, workflowFilter]);

  const appointmentId = (appointment) => getEntityId(appointment);
  const proposalSalonId = getEntityId(proposalAppointment?.salon);
  const { data: proposalServices = [], isFetching: loadingProposalServices } = useServicesSalonListQuery(
    proposalSalonId ? { salonId: proposalSalonId } : undefined,
    { skip: !proposalSalonId },
  );
  const { data: proposalTeamMembers = [], isFetching: loadingProposalEmployees } = useTeamsTeamMembersByServiceSalonRetrieveQuery(
    proposalSalonId && proposalServiceId
      ? { salon: proposalSalonId, service: Number(proposalServiceId) }
      : undefined,
    { skip: !proposalSalonId || !proposalServiceId },
  );
  const availabilityArgs = buildProposalAvailabilityArgs({
    appointmentId: appointmentId(proposalAppointment),
    serviceId: proposalServiceId,
    teamMemberId: proposalEmployeeId,
    date: proposalDate,
  });
  const { data: proposalSlotsData, isFetching: loadingProposalSlots } = useAppointmentsAvailableSlotsRetrieveQuery(
    availabilityArgs,
    { skip: !availabilityArgs || loadingProposalEmployees },
  );
  const proposalSlots = useMemo(
    () => proposalSlotsData?.available_slots_iso
      || (proposalSlotsData?.available_slots || []).map((time) =>
        moment(`${proposalDate} ${time}`, 'YYYY-MM-DD HH:mm').format()),
    [proposalSlotsData, proposalDate],
  );

  // Navigation functions
  const goToPrevious = () => {
    setCurrentDate(prev => prev.clone().subtract(1, viewMode === 'day' ? 'day' : viewMode === 'week' ? 'week' : 'month'));
  };

  const goToNext = () => {
    setCurrentDate(prev => prev.clone().add(1, viewMode === 'day' ? 'day' : viewMode === 'week' ? 'week' : 'month'));
  };

  const goToToday = () => {
    setCurrentDate(moment());
  };

  // Get period label
  const getPeriodLabel = () => {
    // For Arabic: week starts on Sunday, for others: week starts on Monday (ISO)
    const weekStart = i18n.language === 'ar' ? 'week' : 'isoWeek';
    
    if (viewMode === 'day') {
      return t('pro_dashboard.appointments.day_label', { date: currentDate.format('dddd D MMMM YYYY') });
    } else if (viewMode === 'week') {
      const startOfWeek = currentDate.clone().startOf(weekStart);
      const endOfWeek = currentDate.clone().endOf(weekStart);
      return `${t('pro_dashboard.appointments.week')} ${startOfWeek.format('D MMM')} - ${endOfWeek.format('D MMM YYYY')}`;
    } else {
      return t('pro_dashboard.appointments.month_label', { month: currentDate.format('MMMM'), year: currentDate.format('YYYY') });
    }
  };

  // Show appointment details
  const handleShowDetails = (appointment) => {
    if (!appointment) {
      console.error('No appointment provided for viewing');
      return;
    }
    setSelectedAppointment(appointment);
  };

  // Handle edit appointment
  const handleEditAppointment = (appointment) => {

    // Ensure we have the correct IDs from the appointment object
    // The API response structure might have different field names than what the form expects
    const formattedAppointment = {
      appointment_id: appointment.appointment_id,
      start_time: appointment.start_time,
      end_time: appointment.end_time,
      status: appointment.status || 'pending',
      // For client, team_member, service, and salon, we need to ensure we have the correct ID
      // The API might return these as nested objects or as direct ID references
      client: (appointment.client && typeof appointment.client === 'object') ? appointment.client.id : appointment.client,
      team_member: (appointment.team_member && typeof appointment.team_member === 'object') ? appointment.team_member.id : appointment.team_member,
      service: (appointment.service && typeof appointment.service === 'object') ? appointment.service.id : appointment.service,
      salon: (appointment.salon && typeof appointment.salon === 'object') ? appointment.salon.id : appointment.salon,
      // Add these fields explicitly to ensure they're available
      client_name: appointment.client_name,
      team_member_name: appointment.team_member_name,
      service_name: appointment.service_name,
      salon_name: appointment.salon_name,
      cancellation_reason: appointment.cancellation_reason,
      confirmOnSave: !!appointment.confirmOnSave
    };

    setAppointmentToUpdate(formattedAppointment);
    setIsUpdateDialogOpen(true);
  };

  // Handle delete appointment
  const handleDeleteAppointment = (appointment) => {
    setAppointmentToDelete(appointment);
    setIsDeleteDialogOpen(true);
  };

  const handleWorkflowSuccess = async () => {
    setWorkflowError('');
    setSelectedAppointment(null);
    setProposalAppointment(null);
    setProposalServiceId('');
    setProposalEmployeeId('');
    setProposalDate('');
    await refetch();
  };
  const handleConfirmAppointment = async (appointment) => {
    setWorkflowError('');
    try {
      await confirmAppointment(appointmentId(appointment)).unwrap();
      await handleWorkflowSuccess();
    } catch (requestError) {
      setWorkflowError(requestError?.data?.detail || t('appointment_workflow.errors.stale_state'));
    }
  };
  const handleOpenProposal = (appointment) => {
    const initial = createInitialProposalForm(appointment);
    setWorkflowError('');
    setProposalAppointment(appointment);
    setProposalServiceId(initial.proposed_service);
    setProposalEmployeeId(initial.proposed_team_member);
    setProposalDate(initial.proposed_date || moment().format('YYYY-MM-DD'));
  };
  const handleSendProposal = async (form) => {
    try {
      const { proposed_date, ...payload } = form;
      await proposeAppointment({ id: appointmentId(proposalAppointment), ...payload }).unwrap();
      await handleWorkflowSuccess();
    } catch (requestError) {
      const code = requestError?.data?.code;
      setWorkflowError(
        code
          ? t(`appointment_workflow.errors.${code}`, {
            defaultValue: requestError?.data?.detail || code,
          })
          : requestError?.data?.detail || t('appointment_workflow.errors.stale_state'),
      );
    }
  };

  return (
    <div>
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: 2 }}>
              <Typography sx={TitleScreen()}>
                {t('pro_dashboard.appointments.title')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  value={workflowFilter}
                  exclusive
                  onChange={(event, value) => value && setWorkflowFilter(value)}
                  size="small"
                >
                  <ToggleButton value="all">{t('appointment_workflow.actions.all')}</ToggleButton>
                  <ToggleButton value="action_required">
                    {t('appointment_workflow.actions.required')} ({actionRequiredCount})
                  </ToggleButton>
                </ToggleButtonGroup>
                {/* View mode selector */}
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 2,
                      py: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: theme.colors.primary[500],
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: theme.colors.primary[600],
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="day" aria-label={t('pro_dashboard.appointments.day')}>
                    <ViewDay sx={{ mr: 0.5, fontSize: 18 }} />
                    {t('pro_dashboard.appointments.day')}
                  </ToggleButton>
                  <ToggleButton value="week" aria-label={t('pro_dashboard.appointments.week')}>
                    <ViewWeek sx={{ mr: 0.5, fontSize: 18 }} />
                    {t('pro_dashboard.appointments.week')}
                  </ToggleButton>
                  <ToggleButton value="month" aria-label={t('pro_dashboard.appointments.month')}>
                    <CalendarToday sx={{ mr: 0.5, fontSize: 18 }} />
                    {t('pro_dashboard.appointments.month')}
                  </ToggleButton>
                </ToggleButtonGroup>
                <Can I="create" a="appointments">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => setIsAddDialogOpen(true)}
                    sx={{
                      backgroundColor: theme.colors.primary[500],
                      '&:hover': {
                        backgroundColor: theme.colors.primary[700],
                      }
                    }}
                  >
                    {t('pro_dashboard.appointments.add_appointment')}
                  </Button>
                </Can>
              </Box>
            </Box>
          }
        />
        <CardContent>
          {deepLinkError && (
            <Alert
              severity="warning"
              onClose={() => setDeepLinkError('')}
              sx={{ mb: 2 }}
            >
              {deepLinkError}
            </Alert>
          )}
          {/* Navigation controls */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            pb: 2,
            borderBottom: `2px solid ${theme.colors.primary[100]}`
          }}>
            <IconButton
              onClick={goToPrevious}
              sx={{
                color: theme.colors.primary[500],
                '&:hover': { backgroundColor: theme.colors.primary[50] }
              }}
            >
              <ChevronLeft />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.colors.text.primary,
                  textTransform: 'capitalize',
                  fontSize: { xs: '0.9rem', md: '1.25rem' }
                }}
              >
                {getPeriodLabel()}
              </Typography>
              <Button
                size="small"
                onClick={goToToday}
                sx={{
                  color: theme.colors.primary[500],
                  '&:hover': { backgroundColor: theme.colors.primary[50] }
                }}
              >
                {t('pro_dashboard.appointments.today')}
              </Button>
            </Box>

            <IconButton
              onClick={goToNext}
              sx={{
                color: theme.colors.primary[500],
                '&:hover': { backgroundColor: theme.colors.primary[50] }
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>

          {isLoading ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '60px 0',
              color: theme.colors.text.secondary
            }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                {t('pro_dashboard.common.loading')}
              </Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('pro_dashboard.common.error')}: {error.message}
            </Alert>
          ) : (
            <>
              {viewMode === 'day' && (
                <DayView
                  appointments={visibleAppointments || []}
                  currentDate={currentDate}
                  onShowDetails={handleShowDetails}
                  onEditAppointment={handleEditAppointment}
                  onDeleteAppointment={handleDeleteAppointment}
                  isEmployee={userProfile?.role === 'professional_employe'}
                />
              )}
              {viewMode === 'week' && (
                <WeekView
                  appointments={visibleAppointments || []}
                  currentDate={currentDate}
                  onShowDetails={handleShowDetails}
                  onEditAppointment={handleEditAppointment}
                  onDeleteAppointment={handleDeleteAppointment}
                  isEmployee={userProfile?.role === 'professional_employe'}
                />
              )}
              {viewMode === 'month' && (
                <MonthView
                  appointments={workflowFilter === 'action_required'
                    ? (appointments || []).filter((item) => ['pending', 'proposal_expired'].includes(item.status))
                    : (appointments || [])}
                  currentDate={currentDate}
                  onShowDetails={handleShowDetails}
                  onEditAppointment={handleEditAppointment}
                  onDeleteAppointment={handleDeleteAppointment}
                  isEmployee={userProfile?.role === 'professional_employe'}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AppointmentDetailsDialog
        open={!!selectedAppointment}
        appointment={selectedAppointment}
        busy={isConfirming || isProposing}
        error={workflowError}
        onClose={() => setSelectedAppointment(null)}
        onConfirm={() => handleConfirmAppointment(selectedAppointment)}
        onModifyConfirm={() => {
          setSelectedAppointment(null);
          handleEditAppointment({ ...selectedAppointment, confirmOnSave: true });
        }}
        onPropose={() => {
          setSelectedAppointment(null);
          handleOpenProposal(selectedAppointment);
        }}
        onCancel={() => {
          setSelectedAppointment(null);
          handleDeleteAppointment(selectedAppointment);
        }}
        labels={{
          title: t('appointment_workflow.details.title'),
          client: t('appointment_workflow.details.client'),
          service: t('appointment_workflow.details.service'),
          employee: t('appointment_workflow.details.employee'),
          salon: t('appointment_workflow.details.salon'),
          dateTime: t('appointment_workflow.details.date_time'),
          duration: t('appointment_workflow.details.duration'),
          price: t('appointment_workflow.details.price'),
          cancellationReason: t('pro_appointments.labels.cancellation_reason'),
          status: t(appointmentStatusLabelKey(selectedAppointment?.status)),
          close: t('common.close'),
          confirm: t('appointment_workflow.actions.confirm'),
          modify: t('appointment_workflow.actions.modify_confirm'),
          propose: t('appointment_workflow.actions.propose'),
          cancel: t('appointment_workflow.actions.refuse_request'),
        }}
      />

      {/* Delete Appointment Dialog */}
      {appointmentToDelete && (
        <DeleteAppointment
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onSuccess={() => {
            refetch();
          }}
          appointment={appointmentToDelete}
        />
      )}

      {/* Update Appointment Dialog */}
      {appointmentToUpdate && (
        <UpdateAppointment
          open={isUpdateDialogOpen}
          onClose={() => setIsUpdateDialogOpen(false)}
          onSuccess={() => {
            // Refresh appointments list after successful update
            refetch();
          }}
          appointment={appointmentToUpdate}
          confirmOnSave={!!appointmentToUpdate.confirmOnSave}
        />
      )}

      <AppointmentProposalDialog
        open={!!proposalAppointment}
        appointment={proposalAppointment}
        services={Array.isArray(proposalServices) ? proposalServices : []}
        teamMembers={Array.isArray(proposalTeamMembers) ? proposalTeamMembers : []}
        availableSlots={proposalSlots}
        busy={isProposing}
        loadingServices={loadingProposalServices}
        loadingEmployees={loadingProposalEmployees}
        loadingSlots={loadingProposalSlots}
        error={workflowError}
        onServiceChange={setProposalServiceId}
        onEmployeeChange={setProposalEmployeeId}
        onDateChange={setProposalDate}
        onClose={() => setProposalAppointment(null)}
        onSubmit={handleSendProposal}
        labels={{
          title: t('appointment_workflow.proposal.title'),
          intro: t('appointment_workflow.proposal.intro'),
          service: t('appointment_workflow.proposal.service'),
          date: t('pro_dashboard.appointments.date'),
          slot: t('appointment_workflow.proposal.slot'),
          employee: t('appointment_workflow.proposal.employee'),
          expiry: t('appointment_workflow.proposal.expiry'),
          message: t('appointment_workflow.proposal.message'),
          deadline: t('appointment_workflow.proposal.deadline'),
          send: t('appointment_workflow.proposal.send'),
          close: t('appointment_workflow.proposal.close'),
          noEmployees: t('appointment_workflow.proposal.no_employees'),
          noSlots: t('appointment_workflow.proposal.no_slots'),
        }}
      />

      {/* Add Appointment Dialog - ne rendre que si le dialogue est ouvert */}
      {isAddDialogOpen && (
        <AddAppointment
          open={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          onSuccess={() => {
            // Refresh appointments list after successful addition
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default Appointment;
